import React, { useCallback, useEffect, useRef, useState, memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FrameProofLink } from 'site-modules/shared/components/ai-link/frame-proof-link';
import { buildAiAssistantUrl } from 'site-modules/shared/utils/ai-assistant-url';
import { Storage } from 'site-modules/shared/utils/storage';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { venomHistory } from 'client/utils/history/venom-history';
import { ENTER_KEY_CODE } from 'site-modules/shared/constants/key-codes';
import { AutosizedArea } from 'site-modules/shared/components/inventory/autosized-area/autosized-area';
import { useChatInputHandlers } from 'site-modules/shared/hooks/use-chat-input-handlers';
import { MicButton } from 'site-modules/shared/components/mic-button/mic-button';

import './co-driver-banner.scss';

const CREATIVE_ID = 'codriver-banner';
const CO_DRIVER_BANNER_DISMISSED = 'co_driver_banner_dismissed';
const CO_DRIVER_BANNER_INPUT_ID = 'co-driver-banner-input';
export const INPUT_PLACEHOLDER = 'Ask anything';

export const CoDriverBanner = memo(({ isSearch534Chal = false }) => {
  const sessionStorage = useRef(new Storage('sessionStorage'));

  const [isVisible, setIsVisible] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);
  const hasScrolled = useRef(false);

  const {
    isListening,
    isSupported,
    isAllowed,
    stopListening,
    handleInputChange,
    handleInputClick,
    handleToggleListening,
  } = useChatInputHandlers({
    inputRef,
    inputValue,
    onInputChange: setInputValue,
    isSpeechEnabled: isSearch534Chal,
  });
  const isButtonEnabled = !!inputValue.length;

  const showBanner = useCallback(() => {
    if (hasScrolled.current) return;

    hasScrolled.current = true;
    setIsVisible(true);

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
      event_data: {
        action_name: TrackingConstant.ACTION_WIDGET_VIEW,
        action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
        total_page_height: Math.max(document.body.scrollHeight, document.body.offsetHeight),
        creative_id: CREATIVE_ID,
      },
    });
  }, []);

  const handleClick = useCallback(() => {
    sessionStorage.current.set(CO_DRIVER_BANNER_DISMISSED, true);
  }, [sessionStorage]);

  const handleClose = useCallback(() => {
    sessionStorage.current.set(CO_DRIVER_BANNER_DISMISSED, true);
    setIsDismissing(true);
  }, [sessionStorage]);

  const handleAnimationEnd = useCallback(() => {
    if (isDismissing) {
      setIsVisible(false);
    }
  }, [isDismissing]);

  useEffect(() => {
    const visible =
      sessionStorage.current.storageInitialized() && !sessionStorage.current.get(CO_DRIVER_BANNER_DISMISSED);

    if (!visible) return undefined;

    if (!isSearch534Chal) {
      showBanner();
      return undefined;
    }

    window.addEventListener('scroll', showBanner, { once: true, passive: true });

    return () => window.removeEventListener('scroll', showBanner);
  }, [isSearch534Chal, showBanner]);

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();

      stopListening();

      if (!inputValue.trim()) return;

      sessionStorage.current.set(CO_DRIVER_BANNER_DISMISSED, true);
      venomHistory.push(
        buildAiAssistantUrl(
          { pathname: window.location.pathname, search: window.location.search },
          { message: inputValue }
        )
      );
    },
    [inputValue, stopListening]
  );
  const handleKeyDown = useCallback(
    event => {
      if (event.keyCode === ENTER_KEY_CODE && !event.shiftKey) {
        handleSubmit(event);
      }
    },
    [handleSubmit]
  );
  const handleInputFocus = useCallback(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_START,
      event_data: {
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_name: TrackingConstant.VIEW_SEARCH_RESULT,
        subaction_name: TrackingConstant.SELECT_AUTOCOMPLETE,
        action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
        creative_id: CREATIVE_ID,
        value: INPUT_PLACEHOLDER,
      },
    });
  }, []);

  if (!isVisible) {
    return null;
  }

  const bannerClassName = classnames(
    'co-driver-card-link banner pos-r d-flex text-decoration-none rounded-12 w-100 with-input',
    { 'with-autosized-input': isSearch534Chal }
  );
  const bannerContent = (
    <span
      className={classnames(
        'co-driver-card-content d-flex flex-column w-100 px-1 pt-1 pb-1 pb-md-1_25 text-blue-100 rounded-12 align-items-center',
        { 'co-driver-banner-challenge-content pos-r': isSearch534Chal }
      )}
    >
      <span className="co-driver-banner w-100">
        <span className="d-flex align-items-center justify-content-start flex-shrink-0 mb-1 pos-r">
          <i className="icon icon-edmunds-car size-20 me-0_25 pos-a left-0 bottom-0" aria-hidden />
          <span className="fw-bold size-14 ms-1_5">AI Assistant</span>
        </span>
        <span className="d-flex align-items-md-center justify-content-center w-100 flex-column flex-md-row">
          {isSearch534Chal ? (
            <label
              className="co-driver-banner-input-label text-white fw-bold w-100 me-md-3 me-1_75 size-24 mb-1 mb-md-0"
              htmlFor={CO_DRIVER_BANNER_INPUT_ID}
            >
              Get the best price. Ask me how.
            </label>
          ) : (
            <span className="text-white fw-bold w-100 me-md-3 me-1_75 size-24 mb-1 mb-md-0">
              Get the best price. Ask me how.
            </span>
          )}
          {isSearch534Chal ? (
            <form className="chat-input-wrapper w-100" onSubmit={handleSubmit}>
              <div className="chat-input-container pos-r">
                <AutosizedArea
                  className="chat-input text-cool-gray-10 size-16 pos-r bg-white w-100 border-0"
                  innerInputRef={inputRef}
                  onChange={handleInputChange}
                  onClick={handleInputClick}
                  onFocus={handleInputFocus}
                  onKeyDown={handleKeyDown}
                  hintText={INPUT_PLACEHOLDER}
                  inputValue={inputValue}
                  inputMinHeight={44}
                  maxLength={5000}
                  id={CO_DRIVER_BANNER_INPUT_ID}
                />
                <div className="tool-buttons pos-a right-0 me-0_25 d-flex align-items-center gap-0_5">
                  <MicButton
                    isListening={isListening}
                    isDisabled={!isAllowed || !isSupported}
                    onClick={handleToggleListening}
                  />
                  <button
                    type="submit"
                    className={classnames(
                      'submit-btn p-0 d-flex align-items-center justify-content-center border-0 rounded-circle',
                      isButtonEnabled ? 'bg-cool-gray-20' : 'bg-cool-gray-60'
                    )}
                    aria-label="Submit question"
                    disabled={!isButtonEnabled}
                  >
                    <span className="visually-hidden">Submit</span>
                    <i className="icon icon-arrow-up8 text-white size-20" aria-hidden />
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <span className="co-driver-input pos-r w-100">
              <span className="co-driver-input-copy w-100 bg-white text-cool-gray-50 ps-1 pe-0_25 d-flex align-items-center justify-content-between">
                {INPUT_PLACEHOLDER}
                <span
                  className="co-driver-submit bg-cool-gray-60 d-flex align-items-center justify-content-center"
                  aria-hidden="true"
                >
                  <i className="icon icon-arrow-up8 size-20 text-white" aria-hidden />
                </span>
              </span>
            </span>
          )}
        </span>
      </span>
    </span>
  );

  return (
    <div
      className={classnames('co-driver-banner-wrapper w-100 fixed-bottom pos-r', {
        dismissing: isDismissing,
      })}
      data-tracking-parent={CREATIVE_ID}
      data-no-widget-view
      role="region"
      aria-label="AI Assistant banner"
      onAnimationEnd={handleAnimationEnd}
    >
      {isSearch534Chal ? (
        <div className={bannerClassName}>
          <FrameProofLink
            className="pos-a top-0 left-0 w-100 h-100"
            ariaLabel="Open AI Assistant chat"
            onClick={handleClick}
            {...(inputValue ? { aiAssistantParams: { message: inputValue } } : {})}
          >
            <span className="visually-hidden">Open AI Assistant chat</span>
          </FrameProofLink>
          {bannerContent}
        </div>
      ) : (
        <FrameProofLink className={bannerClassName} ariaLabel="Open AI Assistant chat" onClick={handleClick}>
          {bannerContent}
        </FrameProofLink>
      )}
      <button
        type="button"
        className="co-driver-banner-close pos-a border-0 bg-transparent"
        aria-label="Close AI Assistant banner"
        onClick={handleClose}
        data-tracking-id="co_driver_banner_dismiss"
      >
        <i
          className="d-inline-block icon icon-cross2 size-12 text-cool-gray-10 bg-cool-gray-80 rounded-circle text-center"
          aria-hidden
        />
      </button>
    </div>
  );
});

CoDriverBanner.displayName = 'CoDriverCardBanner';
CoDriverBanner.propTypes = {
  isSearch534Chal: PropTypes.bool,
};
